import React, { useState, useEffect, useCallback, useRef } from 'react';
import WhatsAppSendButton from '../../components/WhatsAppSendButton';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import api from '../../utils/api';
import { buildImageUrl } from '../../utils/imageUrl';
import { buildMainPoHtml } from './mainPoPrint';
import { exportMoToExcel } from '../../utils/moExcel';
import { QTY_FINISHES, finishQtyOf, restQtyOf, finishQtyError, onItemQtyChange, cleanFinishFields } from '../../utils/finishQty';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Plus, Trash2, Save, Send, Printer, Image as ImageIcon,
  X, Lock, AlertTriangle, CheckCircle2, FolderOpen, Loader2, Hash, Wand2, PenLine, Link2, FileSignature, FileSpreadsheet
} from 'lucide-react';

const emptyItem = () => ({
  _tmp: Math.random().toString(36).slice(2),
  id: null, area: '', proto_code: '', product_name: '', product_size: '',
  finishes_note: '', is_paint_finish: 0, is_fabric_finish: 0, is_metal_finish: 0, paint_qty: null, fabric_qty: null,
  size_length: '', size_width: '', size_thickness: '', qty: 1, remark: '', images: [],
});

const PROTO_MODE_LABEL = { auto: 'Automatic', manual: 'Manual' };

const STATUS_META = {
  draft:     { label: 'Draft', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  submitted: { label: 'Pending Approval', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  approved:  { label: 'Approved — Locked', cls: 'bg-green-100 text-green-700 border-green-300' },
  rejected:  { label: 'Rejected', cls: 'bg-red-100 text-red-700 border-red-300' },
};

export default function MainPOFormPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/worker') ? '/worker/main-po' : '/admin/main-po';
  const { canWrite, canEdit } = usePermission('main_po');
  const { user } = useAuth();
  const { settings } = useSettings() || {};

  const [po, setPo] = useState(null);
  const [header, setHeader] = useState({ po_no: '', project_name: '', client_name: '', pmc_name: '', order_date: '', timelines: '', remarks: '' });
  const [items, setItems] = useState([emptyItem()]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingItem, setUploadingItem] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);
  // Proto Code mode: chosen once for a new order, then fixed for the order and its Project
  const [protoMode, setProtoMode] = useState(null);
  const [preview, setPreview] = useState(null);          // next series codes (auto mode)
  const [addMode, setAddMode] = useState(false);          // adding rows to an approved order
  const [askTransfer, setAskTransfer] = useState(false);  // "add to the linked Project?" popup
  const [transferNote, setTransferNote] = useState(null); // shown after items reach the Project
  const [transferring, setTransferring] = useState(false);
  const [finishAsk, setFinishAsk] = useState(null);       // "How much quantity requires this finish?" popup

  const status = po?.status || 'draft';
  const isLocked = po && !['draft', 'rejected'].includes(status);
  const isCreator = !po || po.created_by === user?.id;
  const isAdmin = user?.role === 'admin';
  // Workers: draft/rejected only. Admins can edit at any stage (also after approval).
  const canModify = (!isLocked || isAdmin) && (isNew ? canWrite : (isAdmin || canEdit || (canWrite && isCreator)));
  const wantEdit = searchParams.get('edit') === '1';
  const [editMode, setEditMode] = useState(isNew || wantEdit);
  const editing = editMode && canModify;
  // Approved order + Project: extra rows can be added (existing rows stay locked)
  const canAddLater = !isNew && status === 'approved' && !!po?.project_id && (canEdit || isAdmin);
  const rowEditable = it => editing || (addMode && it._new);
  // Reference images: draft rows, or rows added after approval
  const rowImagesEditable = it => editing || (status === 'approved' && (canEdit || isAdmin) && !!it.added_after_approval);
  const mode = isNew ? protoMode : (po?.proto_mode || 'manual');
  const isAuto = mode === 'auto';
  const pendingRows = (po?.items || []).filter(i => !i.project_item_id);
  const projectPath = `${location.pathname.startsWith('/worker') ? '/worker' : '/admin'}/projects/${po?.project_id}`;

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await api.get(`/main-pos/${id}`);
      setPo(res.data);
      setHeader({
        po_no: res.data.po_no || '',
        project_name: res.data.project_name || '',
        client_name: res.data.client_name || '',
        pmc_name: res.data.pmc_name || '',
        order_date: res.data.order_date ? res.data.order_date.slice(0, 10) : '',
        timelines: res.data.timelines || '',
        remarks: res.data.remarks || '',
      });
      // DECIMAL columns come back as text ("5.00") — show them as plain numbers
      const n = v => (v === null || v === undefined || v === '' ? v : Number(v));
      setItems((res.data.items || []).map(it => ({ ...it, qty: n(it.qty), paint_qty: n(it.paint_qty), fabric_qty: n(it.fabric_qty), _tmp: `db-${it.id}` })));
      setAddMode(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Manufacturing Order');
      navigate(basePath);
    } finally {
      setLoading(false);
    }
  }, [id, isNew, navigate, basePath]);

  useEffect(() => { load(); }, [load]);

  const unsavedCount = items.filter(it => !it.id).length;
  useEffect(() => {
    if (!isAuto || !unsavedCount) { setPreview(null); return; }
    let live = true;
    api.get('/main-pos/proto-preview', { params: { count: unsavedCount } })
      .then(r => { if (live) setPreview(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [isAuto, unsavedCount]);
  // Preview code for an unsaved row = its position among the unsaved rows
  const previewFor = it => {
    if (!preview || it.id) return '';
    const i = items.filter(x => !x.id).indexOf(it);
    return preview.codes?.[i] || '';
  };

  const setItem = (tmp, patch) =>
    setItems(list => list.map(it => it._tmp === tmp ? { ...it, ...patch } : it));

  // ── Paint / Fabric finish quantity ─────────────────────────────────────────
  // Turning Paint or Fabric on asks how much of the row's Qty needs it; the rest is "without".
  const toggleFinish = (it, k) => {
    const f = QTY_FINISHES.find(x => x.flag === k);
    if (!f) return setItem(it._tmp, { [k]: it[k] ? 0 : 1 });                 // Metal: plain Yes / No
    if (it[k]) return setItem(it._tmp, { [k]: 0, [f.key]: null, [f.rest]: undefined, [f.full]: undefined });
    const total = Number(it.qty);
    if (!(total > 0)) return toast.error('Enter the item Qty first');
    setFinishAsk({ tmp: it._tmp, f, total, value: String(total), product: it.product_name });
  };
  const askError = (() => {
    if (!finishAsk) return null;
    const v = finishAsk.value.trim();
    const n = Number(v);
    if (!v || !Number.isFinite(n)) return 'Enter a quantity';
    if (n <= 0) return 'Quantity must be more than 0';
    if (n > finishAsk.total) return `Can't be more than the item Qty (${finishAsk.total})`;
    return null;
  })();
  const confirmFinishAsk = () => {
    if (!finishAsk || askError) return;
    const { tmp, f } = finishAsk;
    const v = Math.round(Number(finishAsk.value) * 100) / 100;
    setItem(tmp, { [f.flag]: 1, [f.key]: v, [f.rest]: undefined, [f.full]: v >= finishAsk.total });
    setFinishAsk(null);
  };
  const setFinishWith = (it, f, v) => setItem(it._tmp, { [f.key]: v, [f.rest]: undefined, [f.full]: v !== '' && Number(v) === Number(it.qty) });
  const setFinishRest = (it, f, v) => {
    const r = Number(v);
    const patch = { [f.rest]: v, [f.full]: v !== '' && r === 0 };
    if (v !== '' && Number.isFinite(r) && r >= 0) patch[f.key] = Math.round(((Number(it.qty) || 0) - r) * 100) / 100;
    setItem(it._tmp, patch);
  };
  const rowsFinishError = list => {
    for (const it of list) {
      for (const f of QTY_FINISHES) {
        const e = finishQtyError(it, f);
        if (e) return `Row ${items.indexOf(it) + 1}: ${e}`;
      }
    }
    return null;
  };

  const save = async () => {
    if (!header.po_no.trim()) return toast.error('Manufacturing Order No. is required');
    if (!header.project_name.trim()) return toast.error('Project Name is required');
    for (const it of items) {
      if (!it.product_name.trim()) return toast.error('Every item row needs a Product Name');
    }
    const fErr = rowsFinishError(items);
    if (fErr) return toast.error(fErr);
    setSaving(true);
    try {
      const payload = { ...header, items: items.map((it, i) => ({ ...cleanFinishFields(it), sort_order: i })) };
      if (isNew) {
        if (!protoMode) { setSaving(false); return toast.error('Choose Manual or Automatic Proto Code first'); }
        payload.proto_mode = protoMode;
        const res = await api.post('/main-pos', payload);
        toast.success(`Saved as draft (${res.data.po_no})`);
        navigate(`${basePath}/${res.data.id}?edit=1`, { replace: true });
      } else {
        await api.put(`/main-pos/${id}`, payload);
        toast.success('Manufacturing Order updated');
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── extra items on an approved order ─────────────────────────────────────────
  const newRows = items.filter(it => it._new);
  const startAddItems = () => {
    setTransferNote(null);
    setAddMode(true);
    setItems(list => [...list, { ...emptyItem(), _new: true }]);
  };
  const cancelAddItems = () => {
    setAddMode(false);
    setItems(list => list.filter(it => !it._new));
  };
  const saveNewItems = () => {
    if (!newRows.length) return toast.error('Add at least one new row');
    for (const it of newRows) {
      if (!String(it.product_name || '').trim()) return toast.error('Every new row needs a Product Name');
    }
    const fErr = rowsFinishError(newRows);
    if (fErr) return toast.error(fErr);
    setAskTransfer(true);   // Yes / No popup decides if they go to the Project
  };
  const confirmAddItems = async (transfer) => {
    setAskTransfer(false);
    setSaving(true);
    try {
      const res = await api.post(`/main-pos/${id}/add-items`, {
        transfer, items: newRows.map(({ _tmp, _new, images, ...it }) => cleanFinishFields(it)),
      });
      if (res.data.transferred) {
        toast.success(`${res.data.transferred} new item(s) added to Project ${res.data.project_code}`);
        setTransferNote({ count: res.data.transferred, code: res.data.project_code });
      } else {
        toast.success(res.data.message || 'New items saved on the Manufacturing Order');
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');   // rows stay on screen
    } finally {
      setSaving(false);
    }
  };
  const transferPending = async () => {
    if (!window.confirm(`Add ${pendingRows.length} item(s) that are not in the Project yet to Project ${po?.linked_project_code}?`)) return;
    setTransferring(true);
    try {
      const res = await api.post(`/main-pos/${id}/transfer-items`, {});
      toast.success(`${res.data.transferred} item(s) added to Project ${res.data.project_code}`);
      setTransferNote({ count: res.data.transferred, code: res.data.project_code });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const submit = async () => {
    if (!window.confirm('Submit this Manufacturing Order for approval? After approval the PO will be locked and the Project will be created automatically.')) return;
    setSubmitting(true);
    try {
      await api.post(`/main-pos/${id}/submit`);
      toast.success('Submitted for approval');
      setEditMode(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ── image upload ───────────────────────────────────────────────────────────
  const pickImages = (item) => {
    if (!item.id) return toast.error('Save the Manufacturing Order first, then upload images to this row');
    uploadTargetRef.current = item;
    fileInputRef.current?.click();
  };

  const onFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const item = uploadTargetRef.current;
    if (!files.length || !item) return;
    setUploadingItem(item._tmp);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('images', f));
      await api.post(`/main-pos/${id}/items/${item.id}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Image(s) uploaded');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingItem(null);
    }
  };

  const deleteImage = async (img) => {
    if (!window.confirm('Delete this reference image?')) return;
    try {
      await api.delete(`/main-pos/item-images/${img.id}`);
      toast.success('Image deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  // ── print ──────────────────────────────────────────────────────────────────
  // MOZI-themed Manufacturing Order (see mainPoPrint.js). The window opens first,
  // straight from the click, so pop-up blockers allow it.
  const handlePrint = () => {
    if (!po) return;
    const win = window.open('', '_blank');
    if (!win) return toast.error('Allow pop-ups to print');
    win.document.write(buildMainPoHtml(po, settings || {}, window.location.origin));
    win.document.close();
    win.focus();
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Loading…</div>;

  // A new order starts by choosing the Proto Code mode
  if (isNew && !protoMode) {
    return <ProtoModeChooser onPick={setProtoMode} onCancel={() => navigate(basePath)} />;
  }

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-6xl mx-auto">
      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onFilesChosen} />

      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(basePath)} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-800">
              {isNew ? 'New Manufacturing Order' : po?.po_no}
            </h1>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {po && (
                <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_META[status].cls}`}>
                  {STATUS_META[status].label}
                </span>
              )}
              {mode && <ProtoModeBadge mode={mode} />}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isNew && (
            <button onClick={() => navigate(`${location.pathname.startsWith('/worker') ? '/worker' : '/admin'}/quotations?mo=${id}`)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 text-sm font-semibold text-blue-700">
              <FileSignature className="w-4 h-4" /> Quotations
            </button>
          )}
          {!isNew && (
            <button onClick={handlePrint}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm font-semibold text-gray-700">
              <Printer className="w-4 h-4" /> Print
            </button>
          )}
          {!isNew && po && (
            <button onClick={() => exportMoToExcel(po).catch(e => toast.error(e.message))}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-sm font-semibold text-emerald-700">
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </button>
          )}
          {!isNew && po && (
            <WhatsAppSendButton
              documentType="MANUFACTURING_REPORT"
              referenceId={po.po_no || id}
              fileName={`Manufacturing Order ${po.po_no || id}.pdf`}
              getHtml={async () => buildMainPoHtml(po, settings || {}, window.location.origin)}
            />
          )}
          {!isNew && !editing && canModify && (
            <button onClick={() => setEditMode(true)}
              className="px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 text-sm font-semibold">
              {isLocked ? 'Edit (Admin)' : 'Edit'}
            </button>
          )}
          {canAddLater && !addMode && (
            <button onClick={startAddItems}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-300 bg-blue-50 text-blue-700 text-sm font-semibold">
              <Plus className="w-4 h-4" /> Add Items
            </button>
          )}
          {addMode && (
            <>
              <button onClick={cancelAddItems} disabled={saving}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={saveNewItems} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save New Items ({newRows.length})
              </button>
            </>
          )}
          {editing && (
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          )}
          {!isNew && !isLocked && (canWrite || user?.role === 'admin') && (isCreator || user?.role === 'admin') && (
            <button onClick={submit} disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit for Approval
            </button>
          )}
        </div>
      </div>

      {/* Status banners */}
      {status === 'rejected' && po?.rejected_reason && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div><strong>Rejected{po.rejected_by_name ? ` by ${po.rejected_by_name}` : ''}:</strong> {po.rejected_reason}
            <div className="text-xs mt-1">You can edit this Manufacturing Order and submit it again.</div>
          </div>
        </div>
      )}
      {status === 'submitted' && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
          <Lock className="w-4 h-4" /> This Manufacturing Order is pending approval and is locked. The Admin can approve or reject it from the Approvals page.
        </div>
      )}
      {status === 'approved' && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
          <CheckCircle2 className="w-4 h-4" />
          Approved{po?.approved_by_name ? ` by ${po.approved_by_name}` : ''}. Project
          <span className="inline-flex items-center gap-1 font-bold"><FolderOpen className="w-4 h-4" /> {po?.linked_project_code}</span>
          was created automatically. {isAdmin
            ? 'As admin you can still edit this order — changes to rows already in the Project update the Project items too.'
            : <>Existing rows are locked{canAddLater ? ' — use Add Items if the customer orders more.' : '.'}</>}
        </div>
      )}
      {transferNote && (
        <div className="flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          <Link2 className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 min-w-[14rem]"><strong>New items have been added to the Project{transferNote.code ? ` ${transferNote.code}` : ''}.</strong> Please complete chain setting and other required configurations.</span>
          <button onClick={() => navigate(projectPath)} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold">Open Project</button>
          <button onClick={() => setTransferNote(null)} className="p-1 text-blue-500"><X className="w-4 h-4" /></button>
        </div>
      )}
      {status === 'approved' && po?.project_id && pendingRows.length > 0 && !addMode && (
        <div className="flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 min-w-[14rem]">{pendingRows.length} item(s) on this order are <strong>not in the Project</strong> yet.</span>
          {(canEdit || isAdmin) && (
            <button onClick={transferPending} disabled={transferring}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold disabled:opacity-50">
              {transferring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Add to Project
            </button>
          )}
        </div>
      )}
      {isAuto && (editing || addMode) && (
        <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-800">
          <Wand2 className="w-4 h-4 flex-shrink-0" />
          Automatic Proto Code: new rows get the next code of the Settings series when you save{preview?.next ? ` (next: ${preview.next})` : ''}. Saved codes never change.
        </div>
      )}

      {/* Finish sheet quick links (Phase 2) */}
      {!isNew && po && (() => {
        const flags = [
          ['is_paint_finish', 'paint', 'Paint Finish'],
          ['is_fabric_finish', 'fabric', 'Fabric Finish'],
          ['is_metal_finish', 'metal', 'Metal Finish'],
        ].map(([col, t, label]) => {
          const qf = QTY_FINISHES.find(x => x.flag === col);
          const list = (po.items || []).filter(i => i[col]);
          return { t, label, n: list.length, q: qf ? list.reduce((s, i) => s + finishQtyOf(i, qf), 0) : null };
        }).filter(f => f.n > 0);
        if (!flags.length) return null;
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="text-sm font-bold text-gray-700 uppercase mb-2">Finish Sheets</h2>
            <div className="flex flex-wrap gap-2">
              {flags.map(f => (
                <button key={f.t} onClick={() => navigate(`${basePath}/${id}/finish/${f.t}`)}
                  className="px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-sm font-semibold hover:bg-blue-100">
                  {f.label} ({f.n} item{f.n > 1 ? 's' : ''}{f.q != null ? ` · Qty ${Math.round(f.q * 100) / 100}` : ''})
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Header form */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5">
        <h2 className="text-sm font-bold text-gray-700 uppercase mb-3">PO Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Manufacturing Order No. *">
            <input value={header.po_no} disabled={!editing} maxLength={50}
              onChange={e => setHeader(h => ({ ...h, po_no: e.target.value }))}
              className="inp font-semibold" placeholder="e.g. MO-2026-0118" />
          </Field>
          <Field label="Project Name *">
            <input value={header.project_name} disabled={!editing}
              onChange={e => setHeader(h => ({ ...h, project_name: e.target.value }))}
              className="inp" placeholder="e.g. ITC Indore Phase 3" />
          </Field>
          <Field label="Client Name">
            <input value={header.client_name} disabled={!editing}
              onChange={e => setHeader(h => ({ ...h, client_name: e.target.value }))}
              className="inp" placeholder="Client name" />
          </Field>
          <Field label="PMC Name">
            <input value={header.pmc_name} disabled={!editing}
              onChange={e => setHeader(h => ({ ...h, pmc_name: e.target.value }))}
              className="inp" placeholder="PMC name" />
          </Field>
          <Field label="Order Date">
            <input type="date" value={header.order_date} disabled={!editing}
              onChange={e => setHeader(h => ({ ...h, order_date: e.target.value }))} className="inp" />
          </Field>
          <Field label="Timelines">
            <input value={header.timelines} disabled={!editing}
              onChange={e => setHeader(h => ({ ...h, timelines: e.target.value }))}
              className="inp" placeholder="e.g. 45 days / 30-09-2026" />
          </Field>
          <Field label="Remarks">
            <input value={header.remarks} disabled={!editing}
              onChange={e => setHeader(h => ({ ...h, remarks: e.target.value }))}
              className="inp" placeholder="Optional remarks" />
          </Field>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-700 uppercase">Item Rows ({items.length})</h2>
          {addMode && (
            <button onClick={() => setItems(list => [...list, { ...emptyItem(), _new: true }])}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold">
              <Plus className="w-3.5 h-3.5" /> Add Another Row
            </button>
          )}
          {editing && (
            <button onClick={() => setItems(list => [...list, emptyItem()])}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold">
              <Plus className="w-3.5 h-3.5" /> Add Row
            </button>
          )}
        </div>

        {items.map((it, idx) => (
          <div key={it._tmp} className="border border-gray-200 rounded-lg p-3 md:p-4 space-y-3 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-bold text-gray-500">ROW {idx + 1}</span>
                {it._new && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold">NEW</span>}
                {!it._new && it.added_after_approval ? <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-bold">Added after approval</span> : null}
                {!it._new && status === 'approved' && po?.project_id && !it.project_item_id && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">Not in Project</span>}
              </span>
              {addMode && it._new && newRows.length > 1 && (
                <button onClick={() => setItems(list => list.filter(x => x._tmp !== it._tmp))}
                  className="p-1 rounded hover:bg-red-50 text-red-500" title="Remove row">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              {editing && items.length > 1 && !it.project_item_id && (
                <button onClick={() => setItems(list => list.filter(x => x._tmp !== it._tmp))}
                  className="p-1 rounded hover:bg-red-50 text-red-500" title="Remove row">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Area"><input value={it.area || ''} disabled={!rowEditable(it)} onChange={e => setItem(it._tmp, { area: e.target.value })} className="inp" placeholder="e.g. 02 ADD GF" /></Field>
              <Field label={isAuto ? 'Proto Code (Auto)' : 'Proto Code'}>
                {isAuto
                  ? <input value={it.proto_code || previewFor(it)} disabled className={`inp ${!it.proto_code ? 'italic text-purple-600' : 'font-semibold'}`}
                      placeholder="Assigned on save" title={it.proto_code ? 'Assigned automatically' : 'Preview — assigned when you save'} />
                  : <input value={it.proto_code || ''} disabled={!rowEditable(it)} maxLength={100} onChange={e => setItem(it._tmp, { proto_code: e.target.value })} className="inp" placeholder="e.g. PROTO_76354" />}
              </Field>
              <Field label="Product Name *" className="col-span-2"><input value={it.product_name || ''} disabled={!rowEditable(it)} onChange={e => setItem(it._tmp, { product_name: e.target.value })} className="inp" placeholder="Product name" /></Field>
              <Field label="Product Size"><input value={it.product_size || ''} disabled={!rowEditable(it)} onChange={e => setItem(it._tmp, { product_size: e.target.value })} className="inp" placeholder="Overall size" /></Field>
              <Field label="Length (mm)"><input value={it.size_length || ''} disabled={!rowEditable(it)} onChange={e => setItem(it._tmp, { size_length: e.target.value })} className="inp" placeholder="L" /></Field>
              <Field label="Width (mm)"><input value={it.size_width || ''} disabled={!rowEditable(it)} onChange={e => setItem(it._tmp, { size_width: e.target.value })} className="inp" placeholder="W" /></Field>
              <Field label="Thickness (mm)"><input value={it.size_thickness || ''} disabled={!rowEditable(it)} onChange={e => setItem(it._tmp, { size_thickness: e.target.value })} className="inp" placeholder="T" /></Field>
              <Field label="Qty *"><input type="number" min="0.01" step="0.01" value={it.qty} disabled={!rowEditable(it)} onChange={e => setItem(it._tmp, onItemQtyChange(it, e.target.value))} className="inp" /></Field>
              <Field label="Finishes Detail" className="col-span-2 md:col-span-3">
                <input value={it.finishes_note || ''} disabled={!rowEditable(it)} onChange={e => setItem(it._tmp, { finishes_note: e.target.value })} className="inp" placeholder="e.g. Panel Work / Solid Wood: LED" />
              </Field>
              <Field label="Remark" className="col-span-2 md:col-span-4">
                <input value={it.remark || ''} disabled={!rowEditable(it)} onChange={e => setItem(it._tmp, { remark: e.target.value })} className="inp" placeholder="Row remark" />
              </Field>
            </div>

            {/* Finish flags */}
            <div className="flex flex-wrap gap-2">
              {[
                ['is_paint_finish', 'Paint Finish'],
                ['is_fabric_finish', 'Fabric Finish'],
                ['is_metal_finish', 'Metal Finish'],
              ].map(([k, label]) => (
                <button key={k} type="button" disabled={!rowEditable(it)}
                  onClick={() => toggleFinish(it, k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                    it[k] ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-500 border-gray-300'
                  } ${!rowEditable(it) ? 'opacity-70 cursor-default' : ''}`}>
                  {label}: {it[k] ? 'YES' : 'NO'}
                  {it[k] && QTY_FINISHES.some(f => f.flag === k) ? ` · ${finishQtyOf(it, QTY_FINISHES.find(f => f.flag === k))}` : ''}
                </button>
              ))}
            </div>

            {/* Paint / Fabric: how much of the Qty needs the finish, and the rest without it */}
            {QTY_FINISHES.some(f => it[f.flag]) && (
              <div className="grid gap-2 md:grid-cols-2">
                {QTY_FINISHES.filter(f => it[f.flag]).map(f => {
                  const err = finishQtyError(it, f);
                  const edit = rowEditable(it);
                  const withVal = it[f.key] ?? finishQtyOf(it, f);
                  const restVal = it[f.rest] ?? restQtyOf(it, f);
                  return (
                    <div key={f.key} className={`rounded-lg border p-2.5 ${err ? 'border-red-300 bg-red-50/50' : f.flag === 'is_paint_finish' ? 'border-teal-200 bg-teal-50/40' : 'border-indigo-200 bg-indigo-50/40'}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-bold text-gray-700">{f.label} Qty</span>
                        <span className="text-[11px] text-gray-500">Item Qty {Number(it.qty) || 0}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                          <span className="block text-[10px] font-semibold uppercase text-gray-500 mb-0.5">1 · With {f.short.toLowerCase()}</span>
                          {edit
                            ? <input type="number" min="0.01" step="0.01" value={withVal} onChange={e => setFinishWith(it, f, e.target.value)} className="inp font-semibold" />
                            : <div className="inp font-semibold bg-white">{Number(withVal)}</div>}
                        </label>
                        <label className="block">
                          <span className="block text-[10px] font-semibold uppercase text-gray-500 mb-0.5">2 · Without {f.short.toLowerCase()} (remaining)</span>
                          {edit
                            ? <input type="number" min="0" step="0.01" value={restVal} onChange={e => setFinishRest(it, f, e.target.value)} className="inp" />
                            : <div className="inp bg-white">{Number(restVal)}</div>}
                        </label>
                      </div>
                      {err && <p className="text-[11px] text-red-600 mt-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {err}</p>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Images */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500">Reference Images ({(it.images || []).length})</span>
                {(rowImagesEditable(it) || (addMode && it._new)) && (
                  <button onClick={() => pickImages(it)} disabled={uploadingItem === it._tmp}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-300 text-xs font-semibold text-gray-600 hover:bg-white disabled:opacity-50">
                    {uploadingItem === it._tmp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                    {it.id ? 'Upload Images' : (it._new ? 'Save new items first to upload' : 'Save PO first to upload')}
                  </button>
                )}
              </div>
              {(it.images || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {it.images.map(img => (
                    <div key={img.id} className="relative group">
                      <a href={buildImageUrl(img.image_path)} target="_blank" rel="noreferrer">
                        <img src={buildImageUrl(img.image_path)} alt="reference"
                          className="w-20 h-20 object-cover rounded-lg border border-gray-200" />
                      </a>
                      {rowImagesEditable(it) && (
                        <button onClick={() => deleteImage(img)}
                          className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {finishAsk && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setFinishAsk(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900">How much quantity requires {finishAsk.f.label}?</h3>
            <p className="text-sm text-gray-500 mt-1">
              {finishAsk.product ? <><strong>{finishAsk.product}</strong> · </> : null}Item Qty <strong>{finishAsk.total}</strong>
            </p>
            <input autoFocus type="number" min="0.01" step="0.01" max={finishAsk.total} value={finishAsk.value}
              onChange={e => setFinishAsk(a => ({ ...a, value: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') confirmFinishAsk(); if (e.key === 'Escape') setFinishAsk(null); }}
              className="inp mt-4 text-lg font-semibold" />
            {askError
              ? <p className="text-xs text-red-600 mt-1.5">{askError}</p>
              : <p className="text-xs text-gray-500 mt-1.5">
                  {Number(finishAsk.value) >= finishAsk.total
                    ? `All ${finishAsk.total} get ${finishAsk.f.short.toLowerCase()} finish.`
                    : `${Number(finishAsk.value)} with ${finishAsk.f.short.toLowerCase()} · ${Math.round((finishAsk.total - Number(finishAsk.value)) * 100) / 100} remaining without.`}
                </p>}
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setFinishAsk(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:bg-gray-100">Cancel</button>
              <button onClick={confirmFinishAsk} disabled={!!askError}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50">OK</button>
            </div>
          </div>
        </div>
      )}

      {askTransfer && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0"><FolderOpen className="w-5 h-5 text-blue-600" /></div>
              <div>
                <h3 className="font-bold text-gray-900">Do you want to add the newly added items to the linked Project?</h3>
                <p className="text-sm text-gray-500 mt-1.5">
                  {newRows.length} new item{newRows.length === 1 ? '' : 's'} → Project <strong>{po?.linked_project_code}</strong>.
                  Only these items are added; everything already in the Project stays as it is.
                </p>
                <p className="text-xs text-gray-400 mt-2">Choose <strong>No</strong> to save them on this order only — you can add them to the Project later.</p>
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2 mt-6">
              <button onClick={() => setAskTransfer(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:bg-gray-100">Cancel</button>
              <button onClick={() => confirmAddItems(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50">No</button>
              <button onClick={() => confirmAddItems(true)} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">Yes, add to Project</button>
            </div>
          </div>
        </div>
      )}

      {/* local styles for inputs */}
      <style>{`
        .inp { width:100%; padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; font-size:13px; background:#fff; }
        .inp:disabled { background:#f9fafb; color:#4b5563; }
        .inp:focus { outline:2px solid #bfdbfe; border-color:#3b82f6; }
      `}</style>
    </div>
  );
}

export function ProtoModeBadge({ mode }) {
  const auto = mode === 'auto';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${auto ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-slate-50 text-slate-700 border-slate-300'}`}
      title="Proto Code mode — fixed for this order and its Project">
      <Hash className="w-3 h-3" /> Proto Code: {PROTO_MODE_LABEL[auto ? 'auto' : 'manual']}
    </span>
  );
}

// Asked before a new order: how Proto Codes are given for this order and its Project
function ProtoModeChooser({ onPick, onCancel }) {
  const [next, setNext] = useState('');
  useEffect(() => {
    api.get('/main-pos/proto-preview', { params: { count: 1 } }).then(r => setNext(r.data.next || '')).catch(() => {});
  }, []);
  const Option = ({ value, icon: Icon, title, text }) => (
    <button onClick={() => onPick(value)}
      className="text-left w-full border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50/40 rounded-xl p-4 transition-colors">
      <div className="flex items-center gap-2 font-bold text-gray-900"><Icon className="w-5 h-5 text-blue-600" /> {title}</div>
      <p className="text-sm text-gray-500 mt-1.5">{text}</p>
    </button>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <h2 className="text-lg font-bold text-gray-900">How should Proto Codes be given?</h2>
        <p className="text-sm text-gray-500 mt-1">This choice is saved with the Manufacturing Order and its Project, and can't be changed later.</p>
        <div className="grid gap-3 mt-5">
          <Option value="auto" icon={Wand2} title="Automatic Proto Code"
            text={`Each item gets the next code of the series in Settings${next ? ` — next is ${next}` : ''}. Codes never repeat.`} />
          <Option value="manual" icon={PenLine} title="Manual Proto Code"
            text="You type the Proto Code for every item. Nothing is generated." />
        </div>
        <div className="flex justify-end mt-5">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:bg-gray-100">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
